import type { FC } from 'react';

import { Locale as LocaleCode } from '@eneco/common/application';
import {
  DayPicker as ReactDayPicker,
  PropsBase,
  PropsMulti,
  PropsMultiRequired,
  PropsRange,
  PropsRangeRequired,
  PropsSingle,
  PropsSingleRequired,
} from 'react-day-picker';
import { Locale, enGB, fr, nl } from 'react-day-picker/locale';

import { DayButton } from './components/DayButton';
import { MonthCaption } from './components/MonthCaption';
import { Weekday } from './components/Weekday';
import { dayPickerClass, pagedNavigationClass } from './Datepicker.css';
import { CloseIcon } from '../../icons';
import { useI18nTranslations } from '../../providers/i18n';
import { useLocale } from '../../providers/locale';
import { getClassNames } from '../../util/css';
import { Box } from '../Box/Box';
import { Button } from '../Button/Button';
import { Card } from '../Card/Card';
import { Divider } from '../Divider/Divider';
import { IconButton } from '../IconButton/IconButton';
import { Stack } from '../Stack/Stack';

export const dateLocale: Record<LocaleCode, Locale> = {
  'nl-NL': nl,
  'nl-BE': nl,
  'fr-BE': fr,
  'en-NL': enGB,
  'en-GB': enGB,
};

// eslint-disable-next-line react/jsx-no-useless-fragment
const EmptyNavButton = () => <></>;

export const Datepicker: FC<Props> = ({
  onClose,
  onSave,
  onReset,
  mode = 'single',
  resetButtonLabel,
  saveButtonLabel,
  fromDate,
  fromYear,
  toDate,
  toYear,
  numberOfMonths = 1,
  disabled = [],
  disableWeekends,
  selected,
  onSelect,
  required,
  ...props
}) => {
  const { save: saveA11yLabel, reset: resetA11yLabel, closeCalendar: closeA11yLabel } = useI18nTranslations();
  const locale = useLocale();

  const disabledDates = Array.isArray(disabled) ? disabled : [disabled];

  let endMonth, startMonth;

  if (fromDate) {
    startMonth = fromDate;
    disabledDates.push({ before: fromDate });
  } else if (fromYear) {
    startMonth = new Date(fromYear, 0);
    disabledDates.push({ before: startMonth });
  }

  if (toDate) {
    endMonth = toDate;
    disabledDates.push({ after: toDate });
  } else if (toYear) {
    endMonth = new Date(toYear, 11, 31);
    disabledDates.push({ after: endMonth });
  }

  if (disableWeekends) {
    disabledDates.push({ dayOfWeek: [0, 6] });
  }

  const dayPickerProps = {
    ...props,
    className: getClassNames(dayPickerClass, {
      [pagedNavigationClass]: !!(numberOfMonths && numberOfMonths > 1),
    }),
    locale: dateLocale[locale],
    disabled: disabledDates,
    fixedWeeks: true,
    endMonth,
    startMonth,
    components: {
      DayButton,
      NextMonthButton: EmptyNavButton,
      MonthCaption,
      PreviousMonthButton: EmptyNavButton,
      Weekday,
    },
    numberOfMonths,
  };

  const renderDayPicker = () => {
    if (mode === 'range') {
      return (
        <ReactDayPicker
          {...dayPickerProps}
          mode="range"
          required={required as PropsRange['required']}
          selected={selected as PropsRange['selected']}
          onSelect={onSelect as PropsRange['onSelect']}
        />
      );
    }

    if (mode === 'multiple') {
      return (
        <ReactDayPicker
          {...dayPickerProps}
          mode="multiple"
          required={required as PropsMulti['required']}
          selected={selected as PropsMulti['selected']}
          onSelect={onSelect as PropsMulti['onSelect']}
        />
      );
    }

    return (
      <ReactDayPicker
        {...dayPickerProps}
        mode="single"
        required={required as PropsSingle['required']}
        selected={selected as PropsSingle['selected']}
        onSelect={onSelect as PropsSingle['onSelect']}
      />
    );
  };

  return (
    <Card>
      {onClose ? (
        <>
          <Stack alignX="end">
            <IconButton label={closeA11yLabel} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
        </>
      ) : null}
      {renderDayPicker()}
      {onSave || onReset ? (
        <>
          <Divider />
          <Box padding="6">
            <Stack alignX="start" gap="5" direction="row">
              {onSave ? (
                <Button size="compact" onClick={onSave}>
                  {saveButtonLabel ?? saveA11yLabel}
                </Button>
              ) : null}
              {onReset ? (
                <Button size="compact" action="secondary" type="reset" onClick={onReset}>
                  {resetButtonLabel ?? resetA11yLabel}
                </Button>
              ) : null}
            </Stack>
          </Box>
        </>
      ) : null}
    </Card>
  );
};

interface Props extends Omit<PropsBase, 'locale'> {
  disableWeekends?: boolean;
  fromDate?: Date;
  fromYear?: number;
  /**
   * Displays a close button, fired when button is clicked
   */
  onClose?(): void;
  onReset?(): void;
  onSave?(): void;
  resetButtonLabel?: string;
  saveButtonLabel?: string;
  selected?: PropsSingle['selected'] | PropsMulti['selected'] | PropsRange['selected'];
  onSelect?:
    | PropsSingle['onSelect']
    | PropsSingleRequired['onSelect']
    | PropsMulti['onSelect']
    | PropsMultiRequired['onSelect']
    | PropsRange['onSelect']
    | PropsRangeRequired['onSelect'];
  required?: boolean;
  toDate?: Date;
  toYear?: number;
}
